import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
} from '@mui/material';
import AdminPricingPanel from '../components/dashboard/AdminPricingPanel';
import AdminPerfPanel from '../components/dashboard/AdminPerfPanel';
import { API_BASE } from '../utils/api';

const ROLES = ['reader', 'author', 'reviewer', 'lead_reviewer', 'admin'];

const ROLE_COLORS = {
  reader: 'default',
  author: 'primary',
  reviewer: 'info',
  lead_reviewer: 'warning',
  admin: 'error',
};

export default function Admin() {
  const accessToken = localStorage.getItem('rv_access_token');
  const headers = useMemo(() => ({
    'Content-Type': 'application/json',
    ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
  }), [accessToken]);

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState({});

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const res = await fetch(`${API_BASE}/api/admin/users`, { credentials: 'omit', headers });
        if (!res.ok) throw new Error('Failed to fetch users');
        const data = await res.json();
        setUsers(Array.isArray(data) ? data : []);
      } catch (e) {
        setError('Could not load users.');
      } finally {
        setLoading(false);
      }
    })();
  }, [headers]);

  const handleRoleChange = async (userId, newRole) => {
    setSaving(prev => ({ ...prev, [userId]: true }));
    try {
      const res = await fetch(`${API_BASE}/api/admin/users/${userId}/role`, {
        method: 'PATCH',
        credentials: 'omit',
        headers,
        body: JSON.stringify({ role: newRole }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        alert(err.error || 'Failed to update role');
        return;
      }
      const updated = await res.json();
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, role: updated.role, roles: updated.roles } : u));
    } catch {
      alert('Error updating role.');
    } finally {
      setSaving(prev => ({ ...prev, [userId]: false }));
    }
  };

  const filtered = users.filter(u =>
    !search || (u.username || '').toLowerCase().includes(search.toLowerCase()) ||
    (u.email || '').toLowerCase().includes(search.toLowerCase())
  );

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', px: 4, py: 4 }}>
      <Typography variant="h4" gutterBottom>Admin Panel</Typography>

      {/* User Management */}
      <Typography variant="h5" sx={{ mb: 2 }}>User Management</Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <TextField
        size="small"
        placeholder="Search by username or email..."
        value={search}
        onChange={e => setSearch(e.target.value)}
        sx={{ mb: 2, minWidth: 300 }}
      />

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} sx={{ mb: 6 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell><strong>Username</strong></TableCell>
                <TableCell><strong>Email</strong></TableCell>
                <TableCell><strong>Current Role</strong></TableCell>
                <TableCell><strong>Change Role</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4}>
                    <Typography variant="body2" color="text.secondary">No users found.</Typography>
                  </TableCell>
                </TableRow>
              ) : filtered.map(u => (
                <TableRow key={u.id} hover>
                  <TableCell>{u.username || '—'}</TableCell>
                  <TableCell>{u.email || '—'}</TableCell>
                  <TableCell>
                    <Chip
                      label={u.role}
                      size="small"
                      color={ROLE_COLORS[u.role] || 'default'}
                    />
                  </TableCell>
                  <TableCell>
                    <Select
                      size="small"
                      value={u.role}
                      disabled={!!saving[u.id]}
                      onChange={e => handleRoleChange(u.id, e.target.value)}
                      sx={{ minWidth: 160 }}
                    >
                      {ROLES.map(r => (
                        <MenuItem key={r} value={r}>{r}</MenuItem>
                      ))}
                    </Select>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Pricing Management */}
      <Typography variant="h5" sx={{ mb: 2 }}>Pricing Management</Typography>
      <AdminPricingPanel />

      {/* Performance Tracking */}
      <Typography variant="h5" sx={{ mb: 2, mt: 6 }}>Performance Tracking</Typography>
      <AdminPerfPanel />
    </Box>
  );
}
